/**
 * ラリー地図の表示部
 *
 * 絞り込みバー・ピン・選択中の操作をまとめる。データと開いているピンは呼び出し側が持つので、
 * 常設シートを重ねる画面でも、単体のピッカーとしても同じ見た目で使える。
 */

import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'

import type { RallyMapItem } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import type { Coords } from '@/hooks/useCurrentPosition'
import { cn } from '@/lib/utils'

import { GoogleMapCanvas } from './google-map-canvas'
import { RallyFilterBar, type SortOption } from './rally-filter-bar'
import { RallyMapLayer } from './rally-map-layer'
import type { RallyPlaceGroup } from './rally-place-popup'
import { useFitBounds } from './use-fit-bounds'
import type { RallyPlacesResult } from './use-rally-places'

/** useFitBounds はマップのコンテキスト内でしか使えないので子として置く */
function MapFitter({ points, fitKey }: { points: google.maps.LatLngLiteral[]; fitKey: string }) {
  useFitBounds({ points, padding: 48, maxZoom: 14, fitKey })
  return null
}

type Props = {
  rallyPlaces: RallyPlacesResult
  /** ポップアップを開いている場所。useRallyPlaces の keepPlaceId にも同じ値を渡す */
  activePlaceId: number | null
  onActivate: (placeId: number | null) => void
  selectedPlaceIds: number[]
  onToggle: (place: RallyPlaceGroup) => void
  onClear: () => void
  /** 渡すと絞り込みバーに「すべて選択」を出す。受け取るのは表示中で未選択の場所 */
  onSelectAll?: (places: RallyPlaceGroup[]) => void
  /** ポップアップのラリー行を押したときの動き。省略するとそのラリーの詳細へ飛ぶ */
  onSelectItem?: (item: RallyMapItem) => void
  /** 地図からスタンプを押したあとに呼ぶ。リスト側を引き直す用 */
  onStampChanged?: () => void
  /** 現在地。渡すと青丸を重ねる */
  origin?: Coords | null
  /** 1 つのラリーに絞っているあいだは凡例が意味を持たない */
  showLegend?: boolean
  /** 絞り込みポップオーバーに足す都道府県の選択 */
  prefectures?: string[]
  selectedPrefectures?: string[]
  onTogglePrefecture?: (value: string) => void
  onClearPrefectures?: () => void
  /** 渡すと絞り込みの隣に並び替えアイコンを出す */
  sortOptions?: SortOption[]
  sortValue?: string
  onSortChange?: (value: string) => void
  /** 常設シートを重ねる画面は下が隠れるので上へ出す */
  actionPosition?: 'top' | 'bottom'
  /** 選択中に「解除」と並べて重ねる実行ボタン */
  footer?: ReactNode
}

export const RallyMapPanel = ({
  rallyPlaces,
  activePlaceId,
  onActivate,
  selectedPlaceIds,
  onToggle,
  onClear,
  onSelectAll,
  onSelectItem,
  onStampChanged,
  origin = null,
  showLegend = true,
  prefectures,
  selectedPrefectures,
  onTogglePrefecture,
  onClearPrefectures,
  sortOptions,
  sortValue,
  onSortChange,
  actionPosition = 'bottom',
  footer,
}: Props) => {
  const { isLoading, hasItems, visible, points, fitKey } = rallyPlaces
  const unselected = visible.filter(({ place }) => !selectedPlaceIds.includes(place.placeId))

  return (
    <div className="relative flex h-full flex-col">
      <RallyFilterBar
        className="border-b"
        count={visible.length}
        selectedCount={selectedPlaceIds.length}
        onSelectAll={
          onSelectAll && unselected.length > 0 ? () => onSelectAll(unselected.map(({ place }) => place)) : undefined
        }
        stampFilter={rallyPlaces.stampFilter}
        onStampFilterChange={rallyPlaces.setStampFilter}
        legend={showLegend ? rallyPlaces.legend : []}
        hiddenRallyIds={rallyPlaces.hiddenRallyIds}
        onToggleRally={rallyPlaces.toggleRally}
        onToggleAllRallies={rallyPlaces.setAllRalliesHidden}
        planLegend={rallyPlaces.planLegend}
        hiddenPlanIds={rallyPlaces.hiddenPlanIds}
        onTogglePlan={rallyPlaces.togglePlan}
        onToggleAllPlans={rallyPlaces.setAllPlansHidden}
        prefectures={prefectures}
        selectedPrefectures={selectedPrefectures}
        onTogglePrefecture={onTogglePrefecture}
        onClearPrefectures={onClearPrefectures}
        sortOptions={sortOptions}
        sortValue={sortValue}
        onSortChange={onSortChange}
      />

      <div className="relative min-h-0 flex-1">
        {isLoading && !hasItems ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : visible.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
            表示するスポットがありません
          </div>
        ) : (
          <GoogleMapCanvas className="h-full w-full" mapTypeControl onClick={() => onActivate(null)}>
            <MapFitter points={points} fitKey={fitKey} />

            <RallyMapLayer
              rallyPlaces={rallyPlaces}
              activePlaceId={activePlaceId}
              onActivate={onActivate}
              selectedPlaceIds={selectedPlaceIds}
              onToggle={onToggle}
              onSelectItem={onSelectItem}
              onStampChanged={onStampChanged}
              origin={origin}
            />
          </GoogleMapCanvas>
        )}

        {selectedPlaceIds.length > 0 && (
          <div className={cn('absolute inset-x-3 flex gap-2', actionPosition === 'top' ? 'top-3' : 'bottom-3')}>
            <Button variant="secondary" onClick={onClear}>
              解除
            </Button>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
